import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { API, authHeaders } from "@/lib/api";

function StepPill({ step }) {
  const isDone = step.status === "completed";
  return (
    <div
      data-testid={`trace-step-${step.agent}-${step.status}`}
      className="border border-hm-border bg-hm-bg-1 rounded-md p-3 mono text-[11px] leading-relaxed"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span
          className={
            "h-1.5 w-1.5 rounded-full " +
            (isDone ? "bg-hm-accent shadow-[0_0_10px_var(--hm-accent-glow)]" : "bg-hm-warn animate-pulse")
          }
        />
        <span className="uppercase tracking-[0.18em] text-hm-fg">{step.agent}</span>
        <span className="text-hm-fg-subtle">·</span>
        <span className="text-hm-fg-muted">{step.status}</span>
        {typeof step.latency_ms === "number" && (
          <span className="ml-auto text-hm-fg-subtle">{step.latency_ms}ms</span>
        )}
      </div>
      {step.input && (
        <pre className="whitespace-pre-wrap break-words text-hm-fg-muted">
          in ▸ {typeof step.input === "string" ? step.input : JSON.stringify(step.input)}
        </pre>
      )}
      {step.output_preview !== undefined && step.output_preview !== null && (
        <pre className="whitespace-pre-wrap break-words text-hm-accent mt-1">
          out ▸ {typeof step.output_preview === "string"
            ? step.output_preview
            : JSON.stringify(step.output_preview, null, 2)}
        </pre>
      )}
    </div>
  );
}

function Bubble({ role, content, streaming }) {
  const isUser = role === "user";
  return (
    <div
      data-testid={`message-${role}`}
      className={"flex w-full mb-3 " + (isUser ? "justify-end" : "justify-start")}
    >
      <div
        className={
          "max-w-[75%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap break-words " +
          (isUser
            ? "bg-hm-accent text-hm-bg rounded-br-sm"
            : "bg-hm-bg-1 text-hm-fg border border-hm-border rounded-bl-sm")
        }
      >
        {content}
        {streaming && (
          <span className="ml-1 inline-block h-3 w-1.5 align-middle bg-hm-accent animate-pulse" />
        )}
      </div>
    </div>
  );
}

export default function ChatSurface({ open, onOpenChange }) {
  const [messages, setMessages] = useState([]);
  const [streamContent, setStreamContent] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [trace, setTrace] = useState([]);
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streamContent, open]);

  // Global event: allow other pages to open chat with a preset message
  useEffect(() => {
    const handler = (e) => {
      const preset = e?.detail?.message;
      if (typeof preset === "string" && preset.length > 0) {
        setInput(preset);
      }
      onOpenChange(true);
    };
    window.addEventListener("hm:open-chat-with", handler);
    return () => window.removeEventListener("hm:open-chat-with", handler);
  }, [onOpenChange]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  const send = useCallback(async () => {
    const msg = input.trim();
    if (!msg || isStreaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setStreamContent("");
    setTrace([]);
    setIsStreaming(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({ thread_id: threadId, message: msg }),
      });
      if (!res.ok || !res.body) throw new Error(`stream failed ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let finalText = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let sep;
        while ((sep = buf.indexOf("\n\n")) !== -1) {
          const raw = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          let evType = "message";
          let dataLines = [];
          for (const line of raw.split("\n")) {
            if (line.startsWith("event:")) evType = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;
          let data;
          try {
            data = JSON.parse(dataLines.join("\n"));
          } catch {
            continue;
          }
          if (evType === "token") {
            finalText += data.content;
            setStreamContent(finalText);
          } else if (evType === "agent_step") {
            setTrace((t) => [...t, data]);
          } else if (evType === "meta") {
            setTrace((t) => [...t, { agent: "runtime", status: data.runtime, ...data }]);
          } else if (evType === "done") {
            setThreadId(data.thread_id);
            setMessages((m) => [...m, { role: "assistant", content: finalText }]);
            setStreamContent("");
          } else if (evType === "error") {
            setMessages((m) => [
              ...m,
              { role: "assistant", content: `⚠ ${data.detail || "error"}` },
            ]);
            setStreamContent("");
          }
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: `⚠ ${e.message}` }]);
      setStreamContent("");
    } finally {
      setIsStreaming(false);
    }
  }, [input, isStreaming, threadId]);

  return (
    <>
      {/* Collapsed floating pill */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="pill"
            layout
            data-testid="chat-surface-pill"
            onClick={() => onOpenChange(true)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 min-w-[640px] max-w-[720px] px-5 py-3.5 rounded-full bg-hm-bg-2 border border-hm-border-strong shadow-hm-glow group hover:border-hm-accent-dim transition-colors"
          >
            <Sparkles className="h-4 w-4 text-hm-accent" />
            <span className="text-sm text-hm-fg-muted group-hover:text-hm-fg transition-colors">
              Ask Hail Mary…
            </span>
            <span className="ml-auto flex items-center gap-1.5 mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
              press
              <kbd className="px-1.5 py-0.5 rounded border border-hm-border bg-hm-bg-1 text-hm-fg-muted">
                ⌘J
              </kbd>
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Expanded overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="overlay"
            data-testid="chat-surface-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-40 backdrop-blur-md bg-[var(--hm-overlay)] flex items-end justify-center p-6"
            onClick={close}
          >
            <motion.div
              initial={{ y: 40, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 40, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-[1180px] h-[80vh] rounded-2xl border border-hm-border-strong bg-hm-bg-2 shadow-hm-glow overflow-hidden grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px]"
            >
              {/* Left: messages */}
              <section className="flex flex-col min-h-0 border-r border-hm-border">
                <header className="h-12 flex items-center gap-2 px-5 border-b border-hm-border shrink-0">
                  <Sparkles className="h-4 w-4 text-hm-accent" />
                  <span className="mono text-[11px] uppercase tracking-[0.22em] text-hm-fg-muted">
                    Chat OS
                  </span>
                  <button
                    onClick={close}
                    className="ml-auto h-7 w-7 rounded-md flex items-center justify-center text-hm-fg-subtle hover:text-hm-fg hover:bg-hm-bg-1"
                    data-testid="chat-close"
                    aria-label="Close chat"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </header>
                <div
                  ref={scrollRef}
                  className="flex-1 min-h-0 overflow-y-auto px-6 py-5"
                  data-testid="chat-scroll"
                >
                  {messages.length === 0 && !streamContent && (
                    <div className="mt-16 text-center">
                      <p className="mono text-[11px] uppercase tracking-[0.22em] text-hm-fg-subtle mb-3">
                        new session
                      </p>
                      <p className="text-hm-fg-muted">
                        Ask anything. Try{" "}
                        <span className="text-hm-accent">
                          {`"What's the latest funding in wellness tech?"`}
                        </span>
                      </p>
                    </div>
                  )}
                  {messages.map((m, i) => (
                    <Bubble key={i} role={m.role} content={m.content} />
                  ))}
                  {streamContent && (
                    <Bubble role="assistant" content={streamContent} streaming />
                  )}
                </div>
                <div className="border-t border-hm-border p-3 shrink-0">
                  <div className="flex gap-2 max-w-3xl mx-auto">
                    <input
                      data-testid="chat-input"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send();
                        }
                      }}
                      placeholder="Type a message…"
                      disabled={isStreaming}
                      className="flex-1 h-11 px-4 rounded-md bg-hm-bg-1 border border-hm-border text-hm-fg placeholder:text-hm-fg-subtle focus:outline-none focus:border-hm-accent-dim focus:ring-1 focus:ring-hm-accent-dim/40"
                    />
                    <button
                      data-testid="chat-send-btn"
                      onClick={send}
                      disabled={isStreaming || !input.trim()}
                      className="h-11 px-5 rounded-md bg-hm-accent text-hm-bg font-medium hover:bg-hm-accent-dim disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      {isStreaming ? "…" : "Send"}
                    </button>
                  </div>
                </div>
              </section>

              {/* Right: trace */}
              <aside className="hidden lg:flex flex-col min-h-0 bg-hm-bg" data-testid="chat-trace-panel">
                <div className="h-12 flex items-center border-b border-hm-border px-5 shrink-0">
                  <span className="mono text-[11px] uppercase tracking-[0.22em] text-hm-fg-muted">
                    agent trace
                  </span>
                  <span className="ml-auto mono text-[10px] uppercase tracking-widest text-hm-fg-subtle">
                    live · sse
                  </span>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-2">
                  {trace.length === 0 && (
                    <p className="mono text-[11px] text-hm-fg-subtle">
                      no run yet — send a message to see the coordinator dispatch a sub-agent.
                    </p>
                  )}
                  {trace.map((s, i) => (
                    <StepPill key={i} step={s} />
                  ))}
                </div>
              </aside>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
